<template>
  <v-container fluid class="pa-0" v-if="editor">

    <!-- 🔹 TOOLBAR -->
    <v-card class="pa-2 mb-2">
      <div class="d-flex flex-wrap toolbar">

        <!-- Bold -->
        <v-btn small @click="cmd('toggleBold')" :color="is('bold')">
          <v-icon small>mdi-format-bold</v-icon>
        </v-btn>

        <!-- Italic -->
        <v-btn small @click="cmd('toggleItalic')" :color="is('italic')">
          <v-icon small>mdi-format-italic</v-icon>
        </v-btn>

        <!-- Underline -->
        <v-btn small @click="cmd('toggleUnderline')" :color="is('underline')">
          <v-icon small>mdi-format-underline</v-icon>
        </v-btn>

        <!-- Strike -->
        <v-btn small @click="cmd('toggleStrike')" :color="is('strike')">
          <v-icon small>mdi-format-strikethrough</v-icon>
        </v-btn>

        <!-- Highlight -->
        <v-btn small @click="cmd('toggleHighlight')" :color="is('highlight')">
          <v-icon small>mdi-marker</v-icon>
        </v-btn>

        <!-- Alignment -->
        <v-btn small @click="align('left')" :color="isAlign('left')">
          <v-icon small>mdi-format-align-left</v-icon>
        </v-btn>

        <v-btn small @click="align('center')" :color="isAlign('center')">
          <v-icon small>mdi-format-align-center</v-icon>
        </v-btn>

        <v-btn small @click="align('right')" :color="isAlign('right')">
          <v-icon small>mdi-format-align-right</v-icon>
        </v-btn>

        <!-- Headings -->
        <v-btn small @click="heading(1)" :color="is('heading', { level: 1 })">
          <v-icon small>mdi-format-header-1</v-icon>
        </v-btn>

        <v-btn small @click="heading(2)" :color="is('heading', { level: 2 })">
          <v-icon small>mdi-format-header-2</v-icon>
        </v-btn>

        <!-- Lists -->
        <v-btn small @click="cmd('toggleBulletList')" :color="is('bulletList')">
          <v-icon small>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn small @click="cmd('toggleOrderedList')" :color="is('orderedList')">
          <v-icon small>mdi-format-list-numbered</v-icon>
        </v-btn>

        <!-- Blockquote -->
        <v-btn small @click="cmd('toggleBlockquote')" :color="is('blockquote')">
          <v-icon small>mdi-format-quote-close</v-icon>
        </v-btn>

        <!-- Divider -->
        <v-btn small @click="cmd('setHorizontalRule')">
          <v-icon small>mdi-minus</v-icon>
        </v-btn>

        

        <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="insertImage" />



        <!-- Colors -->
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small v-bind="attrs" v-on="on">
              <v-icon small>mdi-palette</v-icon>
            </v-btn>
          </template>

          <v-card class="pa-2">
            <div class="d-flex flex-wrap">
              <div
                v-for="c in colors"
                :key="c"
                :style="{ background: c }"
                class="color-dot"
                @click="setColor(c)"
              />
            </div>
          </v-card>
        </v-menu>

        <!-- Clear Formatting -->
        <v-btn small @click="clearFormatting">
          <v-icon small>mdi-format-clear</v-icon>
        </v-btn>

        <!-- Undo / Redo -->
        <v-btn small @click="cmd('undo')" :disabled="!editor.can().undo()">
          <v-icon small>mdi-undo</v-icon>
        </v-btn>

        <v-btn small @click="cmd('redo')" :disabled="!editor.can().redo()">
          <v-icon small>mdi-redo</v-icon>
        </v-btn>

      </div>
    </v-card>

    <!-- 🔹 EDITOR AREA -->
    <v-card outlined class="editor-wrapper">
      <v-card-text class="editor-scroll" @mousedown="focusEditorFromBlankArea">
        <editor-content :editor="editor" class="tiptap" />
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";

import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";

import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";

export default {
  name: "TiptapVuetify",
  components: { EditorContent },

  props: {
    value: { type: String, default: "" },
  },

  data() {
    return {
      editor: null,
      colors: ["#000000", "#FF0000", "#1976D2", "#4CAF50", "#9C27B0", "#FFA000"],
    };
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        Highlight,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Image,
        Color,
        TextStyle,
        Table.configure({
          resizable: false
        }),
        TableRow,
        TableHeader,
        TableCell
      ],
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      },
    });
  },

  destroyed() {
    if (this.editor && !this.editor.isDestroyed) {
      this.editor.destroy();
    }
    this.editor = null;
  },

  watch: {
    value(newVal) {
      if (!this.editor) return;
      const cur = this.editor.getHTML();
      if (newVal !== cur) {
        this.editor.commands.setContent(newVal || "", false);
      }
    },
  },

  methods: {
    cmd(action) {
      if (!this.editor) return;
      this.editor.chain().focus()[action]().run();
    },

    heading(level) {
      this.editor.chain().focus().toggleHeading({ level }).run();
    },

    align(direction) {
      this.editor.chain().focus().setTextAlign(direction).run();
    },

    is(name, attrs = {}) {
      return this.editor?.isActive(name, attrs) ? "primary" : "";
    },

    isAlign(dir) {
      return this.editor?.isActive({ textAlign: dir }) ? "primary" : "";
    },

    openImagePicker() {
      this.$refs.imageInput.click();
    },

    insertImage(e) {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        this.editor.chain().focus().setImage({ src: reader.result }).run();
      };
      reader.readAsDataURL(file);
    },

    insertTable() {
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run();
    },

    setColor(color) {
      this.editor.chain().focus().setColor(color).run();
    },

    clearFormatting() {
      this.editor.chain().focus().clearNodes().unsetAllMarks().run();
    },

    focusEditorFromBlankArea(event) {
      if (!this.editor) return;
      if (event.target.closest(".ProseMirror")) return;

      event.preventDefault();
      this.editor.chain().focus("end").run();
    },
  },
};
</script>

<style scoped>
/* Toolbar */
.toolbar {
  overflow-x: auto;
  gap: 4px;
}

/* Editor container */
.editor-wrapper {
  height: 240px;
  display: flex;
  flex-direction: column;
}

.editor-scroll {
  flex: 1;
  display: flex;
  overflow-y: auto;
  padding: 0;
  cursor: text;
}

/* Tiptap area */
.tiptap {
  flex: 1;
  display: flex;
  min-height: 100%;
}

.tiptap ::v-deep .ProseMirror {
  flex: 1;
  min-height: 100%;
  padding: 12px;
  outline: none;
}

.color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  margin: 4px;
  cursor: pointer;
  border: 1px solid #aaa;
}

@media (max-width: 600px) {
  .toolbar {
    flex-wrap: nowrap !important;
    padding-bottom: 4px;
  }

  .toolbar .v-btn {
    flex: 0 0 auto;
    min-width: 34px !important;
    padding: 0 8px !important;
  }

  .editor-wrapper {
    height: 300px;
  }

  .tiptap ::v-deep .ProseMirror {
    padding: 10px;
    word-break: break-word;
  }
}
</style>
